import {useHydrationDone} from '@aboutyou/lazy-imports';
import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {useHover} from '../../../hooks/useHover/useHover';
import {useMatchesMediaQuery} from '../../../hooks/useMatchesMediaQuery/useMatchesMediaQuery';
import {ProductImage} from '../ProductImage/ProductImage';

interface ProductTileHoverProps {
  imgTestId?: string;
  src?: string;
  srcSet?: string;
  sizes?: string;
  padded?: boolean;
  hoverRef: React.RefObject<HTMLAnchorElement | HTMLElement | null>;
  alt?: string;
}

const ProductTileHoverGate: FC<ProductTileHoverProps> = props => {
  const hydrationDone = useHydrationDone();

  if (!hydrationDone) {
    return null;
  }

  return <ProductTileHover {...props} />;
};

const ProductTileHover: FC<ProductTileHoverProps> = ({
  imgTestId = 'productImageHover',
  src,
  hoverRef,
  padded,
  alt,
}) => {
  const supportsHover = useMatchesMediaQuery('(pointer: fine)');
  const {shouldRender, isActive, onLoad} = useHoverImage(hoverRef, src);

  if (!supportsHover) {
    return null;
  }

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      {src && (
        <HoverImage
          dataTestId={imgTestId}
          src={src}
          onLoad={onLoad}
          data-active={isActive}
          padded={padded}
          alt={alt}
          increasePadding
        />
      )}
    </>
  );
};

const HoverImage = styled(ProductImage)`
  &&& {
    position: absolute !important;
    inset: 8px;
    width: calc(100% - 16px);
    padding-top: calc(133% - 16px);
    opacity: 0;
    transform: translateY(3px);
    transition: opacity 300ms ease-in, transform 250ms ease-in;
  }

  &[data-active='false'] {
    opacity: 0 !important;
    transform: translateY(3px) !important;
    animation: none !important;
  }
`;

const useHoverImage = (
  ref: React.RefObject<HTMLAnchorElement | HTMLElement | null>,
  src?: string,
) => {
  const hasImage = Boolean(src);
  const [isActive, setLoaded] = React.useState(false);
  const {mouseOver} = useHover<HTMLAnchorElement | HTMLElement>(
    ref,
    {
      once: isActive,
    },
    [src],
  );
  const activate = React.useCallback(() => {
    requestAnimationFrame(() => setLoaded(true));
  }, [setLoaded]);

  React.useEffect(() => {
    if (mouseOver && !hasImage) {
      activate();
    }
  }, [mouseOver, hasImage, activate]);

  return {
    shouldRender: mouseOver || isActive,
    isActive,
    onLoad: activate,
  };
};

export {HoverImage, ProductTileHoverGate as ProductTileHover};
